import Vue from 'vue';
import Router from 'vue-router';
import store from '@/store/vuex.js';
import i18n from '@/i18n/i18n.js';
import { hasModulo, getTelaDefaultByContext } from '@/functions/Util.js';

import config from './config.js';
import agro from './agro.js';
import promotor from './promotor.js';
import vendas from './vendas.js';
import despesas from './despesas.js';
import colaboradores from './colaboradores.js';
import integracao from './integracao.js';
import b2b from './b2b.js';
import serv from './serv.js';
import jupiter from './jupiter.js';

Vue.use(Router);

// Para carregamento por demanda
let lazyLoad = (view) => {
    return () => import(`@/components/${view}`);
};

const routes = [
    {
        path: '/',
        name: 'home',
        redirect: { name: 'login' },
    },
    {
        path: '/login',
        name: 'login',
        meta: { contexto: 'login', abrev: 'login', title: 'login' },
        component: lazyLoad('Login.vue'),
    },
    {
        path: '/alterar-senha',
        alias: '/recuperar-senha',
        name: 'AlterarSenha',
        meta: { contexto: 'login', abrev: 'login', title: 'alterar-senha' },
        component: lazyLoad('AlterarSenha.vue'),
    },
    ...config,
    ...agro,
    ...promotor,
    ...vendas,
    ...despesas,
    ...colaboradores,
    ...integracao,
    ...b2b,
    ...serv,
    ...jupiter,
    // rota de erro
    {
        path: '/:pathMatch(.*)*',
        name: 'nao-encontrado',
        meta: {
            contexto: 'config',
            abrev: 'conf',
            title: 'Page not found',
        },
        component: lazyLoad('Error404Page.vue'),
    },
];

const router = new Router({
    mode: 'history',
    routes,
    linkActiveClass: 'active',
    linkExactActiveClass: 'cur-active',
});

/* Navigation guard que é executado antes de entrar em qualquer rota. Referência:
   https://router.vuejs.org/guide/advanced/navigation-guards.html#global-before-guards */
router.beforeEach(async (to, from, next) => {
    store.dispatch('accessDenied', { value: false, title: null });

    /* A função é async/await para esperar a instância do Vue popular os dados
    necessários, então o $root fica disponível em router.app
    Isto só acontece ao carregar pela primeira vez, a partir disto em diante
    os dados já estão presentes, por isso o if. 
    */
    if (!router.app.user) {
        await Vue.nextTick();
    }

    // caso esteja tentando ir da mesma rota para ela mesma cancela a ída
    if (from.fullPath === to.fullPath) {
        next(false);
        return;
    }

    if (process.env.NODE_ENV === 'development') {
        console.log('Indo para a rota:', to);
        // console.log('token', $cookies.get('token'));
        // console.log('version', $cookies.get('version'));
    }

    if ($cookies.get('token') != null && $cookies.get('version') != null) {
        // reseta o acesso ao mudar de rota
        store.dispatch('accessDenied', { value: false, title: null });

        // Armazenar contexto da rota
        if (to.meta.contexto) {
            if (
                !['config', 'colaborador', 'integracao', 'login'].includes(to.meta.contexto) ||
                (to.name == 'login' && $cookies.get('token') != null)
            ) {
                let hasContext = store.state.app_modulo.data.app_moduloList.find(
                    (f) => f.nome == to.meta.contexto && f.permite
                );
                // console.log('hasContext', hasContext);
                
                if (
                    hasContext == undefined &&
                    store.state.app_modulo.data.app_moduloList &&
                    store.state.app_modulo.data.app_moduloList.length > 0
                ) {
                    let firstAllowedContext = store.state.app_modulo.data.app_moduloList[0];

                    console.log(
                        `Contexto não permitido. Redirecionando para ${firstAllowedContext.nome}`
                    );
                    store.dispatch('context', { value: firstAllowedContext.nome });
                    $cookies.set('context', firstAllowedContext.nome);

                    let replaceBy = getTelaDefaultByContext(store.state.context);

                    next({ name: replaceBy, replace: true });
                    return;
                }
                store.dispatch('context', { value: to.meta.contexto });
                $cookies.set('context', to.meta.contexto);
            }
        }

        if (to.meta.admin && !store.state.user.admin) {
            router.app.showMessage('alert', 'msg-acesso-nao-permitido-conf');
            console.log(
                `Rota ${to.name} necessita permissão de administrador. 
            O usuário não possui esta permissão. Redirecionando para o cockpit.`
            );

            let permissao = store.state.user.permissao;
            let context = null;

            if (permissao.permitevendas) {
                context = 'vendas';
            } else if (permissao.permitepromotor) {
                context = 'promotor';
            } else if (permissao.permitedespesas) {
                context = 'despesas';
            } else if (permissao.permiteagro) {
                context = 'agro';
            } else if (permissao.permiteb2b) {
                context = 'b2b';
            } else if (permissao.permiteserv) {
                context = 'prestacao-servico';
            } else if (permissao.permitejupiter) {
                context = 'jupiter';
            }

            let replaceBy = getTelaDefaultByContext(context);
            if (replaceBy != null) {
                next({ name: replaceBy, replace: true });
                return;
            }
        }

        // Validação do MODULOPDA se a rota precisa de um MODULOPDA existente para ser acessada
        if (to.meta.modulo) {
            console.log('to.meta.modulo', to.meta.modulo);
            let has_modulo = hasModulo(to.meta.modulo);

            console.log(
                `Rota ${to.name} necessita do módulo ${to.meta.modulo}. ${
                    has_modulo ? 'Módulo configurado.' : 'Módulo não configurado.'
                }`
            );

            if (!has_modulo) {
                router.app.showMessage('alert', 'msg-acesso-nao-permitido');
                let replaceBy = getTelaDefaultByContext(to.meta.contexto);

                if (replaceBy != null) {
                    next({ name: replaceBy, replace: true });
                    return;
                }
            }
        }
        next();
    } else if (to.name != 'login' && to.name != undefined) {
        store.dispatch('authenticated', { value: false });
        next({ name: 'login', replace: true });
    } else {
        next();
    }
});

router.afterEach((to) => {
    Vue.nextTick(() => {
        // concatena o título com título da rota
        let title = to.meta.title ? i18n.t(to.meta.title) : to.name;
        document.title = `${store.state.partnerSystemHasLayout.partner_nome} • ${title}`;
    });
});

export default router;
